import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LifeBuoy, X, AlertTriangle, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getRecentErrors } from "@/lib/errorTracker";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "hardware", label: "Hardware" },
  { value: "software", label: "Software" },
  { value: "account_access", label: "Account access" },
  { value: "network_wifi", label: "Network / Wi-Fi" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone / mobile" },
  { value: "applications_systems", label: "Applications / systems" },
  { value: "other", label: "Other" },
];

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

/**
 * Map the current pathname to a sensible default ticket category so IT can
 * triage faster. Users can still override before submitting.
 */
function inferCategory(pathname) {
  if (!pathname) return "other";
  const p = pathname.toLowerCase();
  if (p.startsWith("/auth") || p.startsWith("/login") || p.startsWith("/mfa") || p.includes("password")) {
    return "account_access";
  }
  // Anything inside the app shell — Care Seekers, intakes, referrals, etc.
  const appRoutes = [
    "/intakes", "/care-seekers", "/care-plans", "/notes", "/referrals",
    "/appointments", "/outreach", "/maternal", "/resources", "/reports",
    "/retention", "/timesheets", "/tasks", "/volunteers", "/users",
    "/trainings", "/onboarding", "/attachments", "/dashboard", "/inbox",
  ];
  if (appRoutes.some((r) => p === r || p.startsWith(`${r}/`))) {
    return "applications_systems";
  }
  return "other";
}

/**
 * Floating "Need help?" beacon — visible on every authenticated page except
 * the Tech Support page itself. Opens a minimal new-ticket modal that posts
 * directly to /api/tickets. After submit, toasts a success message with the
 * ticket display id.
 */
export default function HelpBeacon() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Allow QuickAddFab (or any component) to open this dialog by
  // dispatching a 'help-beacon:open' window event — no prop drilling.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = () => setOpen(true);
    window.addEventListener("help-beacon:open", handler);
    return () => window.removeEventListener("help-beacon:open", handler);
  }, []);

  // The floating button was retired — the dialog is now opened only via
  // the QuickAdd FAB ("Need help?" entry). Skip rendering on /tech-support
  // (users are already in the ticket queue) or when signed out.
  if (!user) return null;
  if (location.pathname.startsWith("/tech-support")) return null;

  return open ? <HelpBeaconDialog onClose={() => setOpen(false)} /> : null;
}

function HelpBeaconDialog({ onClose }) {
  const location = useLocation();
  // Snapshot recent errors ONCE at dialog open. The user is reporting the
  // moment they decided to ask for help; later async errors shouldn't
  // suddenly bump the priority while they're filling out the form.
  const [recentErrors] = useState(() => getRecentErrors(30_000));
  const hasErrors = recentErrors.length > 0;
  const [form, setForm] = useState(() => {
    const errorSummary = hasErrors
      ? "\n\n— Auto-captured by the Help beacon —\n" +
        recentErrors.slice(-5).map((e) => {
          if (e.kind === "api_error") {
            return `· API ${e.status || "?"} on ${e.url || "?"}${e.message ? ` — ${e.message}` : ""}`;
          }
          return `· JS error: ${e.message}${e.source ? ` (${e.source})` : ""}`;
        }).join("\n")
      : "";
    return {
      title: "",
      description: errorSummary ? errorSummary.trimStart() : "",
      category: inferCategory(location.pathname),
      priority: hasErrors ? "high" : "medium",
      affected_device: "",
    };
  });
  const [saving, setSaving] = useState(false);
  const autoCategory = inferCategory(location.pathname);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Please add a short title for the issue.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        category: form.category,
        priority: form.priority,
        affected_device: form.affected_device.trim() || null,
        affected_url: typeof window !== "undefined" ? window.location.href : location.pathname,
      };
      const { data } = await api.post("/tickets", payload);
      toast.success(`Ticket ${data.display_id || ""} logged — we'll be in touch shortly.`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-[#0F172A]/40 overflow-y-auto"
      data-testid="help-beacon-dialog"
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E5E0]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center">
              <LifeBuoy className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              <div className="hw-eyebrow text-[10px]">Need help?</div>
              <h2 className="font-display text-lg font-semibold text-[#0F172A] mt-0.5">Submit an IT support request</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F9F8F6]" data-testid="help-beacon-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4" data-testid="help-beacon-form">
          {hasErrors && (
            <div
              className="rounded-xl bg-[#FEE2E2] border border-[#FCA5A5] p-3 text-xs text-[#991B1B] flex items-start gap-2"
              data-testid="help-beacon-error-detected"
            >
              <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
              <p>
                <strong>Errors detected on this page</strong> — pre-filled as <strong>High priority</strong>. We attached the technical details below so IT can investigate faster.
              </p>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">What&apos;s going on?</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="help-beacon-title"
              placeholder="e.g., I can't open referrals on my iPad"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">More detail <span className="normal-case text-[#1E3A8A] font-normal">(optional but helpful)</span></label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="help-beacon-description"
              placeholder="What were you trying to do? Any error messages?"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">
                Category
                {form.category === autoCategory && autoCategory !== "other" && (
                  <span className="normal-case text-[#1E3A8A] font-normal ml-1" data-testid="help-beacon-category-auto">· auto-tagged from current page</span>
                )}
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="help-beacon-category"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              >
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                data-testid="help-beacon-priority"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              >
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Device <span className="normal-case text-[#1E3A8A] font-normal">(optional)</span></label>
            <input
              value={form.affected_device}
              onChange={(e) => setForm({ ...form, affected_device: e.target.value })}
              data-testid="help-beacon-device"
              placeholder="MacBook · iPhone 15 · iPad mini"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
            />
          </div>

          <div className="rounded-xl bg-[#E0E7FF] p-3 text-xs text-[#1E3A8A] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
            <p>
              The current page URL is captured automatically so IT can reproduce the issue. You&apos;ll get an email confirmation and a copy of every reply.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#334155]">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="help-beacon-submit"
              className="px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60"
            >
              {saving ? "Sending…" : "Send request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
